import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import { ExpandMore, Close, FilterList } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { CallListParams } from 'retell-sdk/resources';
import { useQuery } from '@tanstack/react-query';

import { RangeFilter } from '../../api/hooks/calls/getCallList';
import { getClients } from '../../api/hooks/clients/getClients';

interface RetellFiltersComponentProps {
  filterCriteria: CallListParams.FilterCriteria & {
    clientId?: string;
    fileNumber?: string;
  };
  setFilterCriteria: React.Dispatch<
    React.SetStateAction<
      CallListParams.FilterCriteria & { clientId?: string; fileNumber?: string }
    >
  >;
  resetPagination: () => void;
}

const RetellFiltersComponent: React.FC<RetellFiltersComponentProps> = ({
  filterCriteria,
  setFilterCriteria,
  resetPagination,
}) => {
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [tempFilterCriteria, setTempFilterCriteria] = useState<
    CallListParams.FilterCriteria & { clientId?: string; fileNumber?: string }
  >({});
  const [startTimeFrom, setStartTimeFrom] = useState<Date | null>(null);
  const [startTimeTo, setStartTimeTo] = useState<Date | null>(null);
  const [toNumber, setToNumber] = useState<string>('');
  const [fromNumber, setFromNumber] = useState<string>('');
  const [clientId, setClientId] = useState<string>('');
  const [fileNumber, setFileNumber] = useState<string>('');

  const {
    data: clientIds = [],
    isLoading: isLoadingClients,
    isError: isClientsError,
  } = useQuery({
    queryKey: ['clientIds'],
    queryFn: () => getClients(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: filterDialogOpen,
  });

  const handleFilterDialogOpen = () => {
    setTempFilterCriteria({ ...filterCriteria });
    setFilterDialogOpen(true);

    setToNumber(
      filterCriteria.to_number && filterCriteria.to_number.length > 0
        ? filterCriteria.to_number[0]
        : ''
    );
    setFromNumber(
      filterCriteria.from_number && filterCriteria.from_number.length > 0
        ? filterCriteria.from_number[0]
        : ''
    );
    setClientId(filterCriteria.clientId || '');
    setFileNumber(filterCriteria.fileNumber || '');

    const startTimestamp = filterCriteria.start_timestamp as
      | RangeFilter
      | undefined;
    if (startTimestamp?.lower_threshold) {
      setStartTimeFrom(new Date(startTimestamp.lower_threshold));
    } else {
      setStartTimeFrom(null);
    }

    if (startTimestamp?.upper_threshold) {
      setStartTimeTo(new Date(startTimestamp.upper_threshold));
    } else {
      setStartTimeTo(null);
    }
  };

  const handleFilterDialogClose = () => {
    setFilterDialogOpen(false);
  };

  const handleApplyFilters = () => {
    const newFilterCriteria = { ...tempFilterCriteria };

    // Process phone numbers
    if (toNumber.trim()) {
      newFilterCriteria.to_number = [toNumber.trim()];
    } else {
      delete newFilterCriteria.to_number;
    }

    if (fromNumber.trim()) {
      newFilterCriteria.from_number = [fromNumber.trim()];
    } else {
      delete newFilterCriteria.from_number;
    }

    // Process client ID
    if (clientId) {
      newFilterCriteria.clientId = clientId;
    } else {
      delete newFilterCriteria.clientId;
    }

    // Process file number
    if (fileNumber.trim()) {
      newFilterCriteria.fileNumber = fileNumber.trim();
    } else {
      delete newFilterCriteria.fileNumber;
    }

    resetPagination();
    setFilterCriteria(newFilterCriteria);
    setFilterDialogOpen(false);
  };

  const handleClearFilters = () => {
    setTempFilterCriteria({});
    setStartTimeFrom(null);
    setStartTimeTo(null);
    setToNumber('');
    setFromNumber('');
    setClientId('');
    setFileNumber('');
  };

  useEffect(() => {
    if (startTimeFrom || startTimeTo) {
      const range: RangeFilter = {};

      if (startTimeFrom) {
        range.lower_threshold = startTimeFrom.getTime();
      }

      if (startTimeTo) {
        range.upper_threshold = startTimeTo.getTime();
      }

      setTempFilterCriteria((prev) => ({
        ...prev,
        start_timestamp: Object.keys(range).length > 0 ? range : undefined,
      }));
    } else {
      setTempFilterCriteria((prev) => {
        const newCriteria = { ...prev };
        delete newCriteria.start_timestamp;
        return newCriteria;
      });
    }
  }, [startTimeFrom, startTimeTo]);

  useEffect(() => {
    if (clientId) {
      setTempFilterCriteria((prev) => ({
        ...prev,
        clientId: clientId,
      }));
    } else {
      setTempFilterCriteria((prev) => {
        const newCriteria = { ...prev };
        delete newCriteria.clientId;
        return newCriteria;
      });
    }
  }, [clientId]);

  useEffect(() => {
    if (fileNumber.trim()) {
      setTempFilterCriteria((prev) => ({
        ...prev,
        fileNumber: fileNumber.trim(),
      }));
    } else {
      setTempFilterCriteria((prev) => {
        const newCriteria = { ...prev };
        delete newCriteria.fileNumber;
        return newCriteria;
      });
    }
  }, [fileNumber]);

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleFilterDialogOpen}
        startIcon={<FilterList />}
        color={'inherit'}
      >
        Filters
      </Button>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={handleFilterDialogClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            Filter Call History
            <IconButton onClick={handleFilterDialogClose} aria-label="close">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Client ID Filter */}
              <FormControl fullWidth>
                <InputLabel id="client-select-label">Client</InputLabel>
                <Select
                  labelId="client-select-label"
                  id="client-select"
                  value={clientId}
                  label="Client"
                  onChange={(e) => setClientId(e.target.value as string)}
                  disabled={isLoadingClients}
                  startAdornment={
                    isLoadingClients ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  <MenuItem value="">
                    <em>All Clients</em>
                  </MenuItem>
                  {clientIds &&
                    clientIds.map((id) => (
                      <MenuItem key={id} value={id}>
                        {id}
                      </MenuItem>
                    ))}
                </Select>
                {isClientsError && (
                  <FormHelperText error>
                    Error loading clients. Please try again.
                  </FormHelperText>
                )}
              </FormControl>

              {/* File Number Filter */}
              <TextField
                label="File Number"
                variant="outlined"
                fullWidth
                value={fileNumber}
                onChange={(e) => setFileNumber(e.target.value)}
                placeholder="Search by file number..."
              />

              {/* Phone Number Filters */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <TextField
                    label="To Number"
                    variant="outlined"
                    fullWidth
                    value={toNumber}
                    onChange={(e) => setToNumber(e.target.value)}
                    placeholder="e.g. +14155551234"
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <TextField
                    label="From Number"
                    variant="outlined"
                    fullWidth
                    value={fromNumber}
                    onChange={(e) => setFromNumber(e.target.value)}
                    placeholder="e.g. +14155551234"
                  />
                </Box>
              </Box>

              {/* Time Range Filters */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Time Range Filters</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Call Start Time
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <DateTimePicker
                          label="From"
                          value={startTimeFrom}
                          onChange={(newValue) => setStartTimeFrom(newValue)}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 300px' }}>
                        <DateTimePicker
                          label="To"
                          value={startTimeTo}
                          onChange={(newValue) => setStartTimeTo(newValue)}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearFilters} color="inherit">
            Clear All Filters
          </Button>
          <Button onClick={handleFilterDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleApplyFilters}
            variant="contained"
            color="primary"
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RetellFiltersComponent;
